import { DeleteOutlined, PictureOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Image, Input, Space, Table, Tooltip, Typography, Upload } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type React from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useImageUploadProps } from './ImageUpload';

const { Text } = Typography;

const THUMBNAIL_SIZE = 48;
const UPLOADS_SEGMENT = '/uploads/';
const SPONSOR_LOGO_MAX_MB = 2;

/**
 * A saját feltöltés útvonala `.../uploads/`-tól (#171). A backend host minden
 * sorban ugyanaz, csak a táblázatot szélesítené; a teljes cím tooltipben
 * marad. Külső webcímnél nincs mit levágni, az egészben látszik.
 */
export const shortLogoPath = (url: string): string => {
  const index = url.indexOf(UPLOADS_SEGMENT);
  return index > 0 ? `...${url.slice(index)}` : url;
};

interface SponsorLogoRow {
  index: number;
  url: string;
}

interface SponsorLogoTableProps {
  logos: string[];
  /**
   * A state setter, not a plain callback: an upload finishes long after the
   * click, and an array built from that render's `logos` would overwrite a
   * row changed in the meantime (another upload, a delete).
   */
  onChange: Dispatch<SetStateAction<string[]>>;
}

interface RowProps {
  url: string;
  onChange: (url: string) => void;
}

const LogoUploadButton: React.FC<RowProps> = ({ url, onChange }) => {
  const { t } = useTranslation();
  const { uploadProps, loading } = useImageUploadProps({
    uploadUrl: '/api/upload/logo',
    fieldName: 'logo',
    maxSize: SPONSOR_LOGO_MAX_MB,
    onUploaded: onChange,
  });

  return (
    <Tooltip title={t('imageUpload.sizeHint', { maxSize: SPONSOR_LOGO_MAX_MB })}>
      <Upload {...uploadProps}>
        <Button icon={<UploadOutlined />} loading={loading}>
          {url ? t('raffle.settings.replaceSponsorLogo') : t('imageUpload.upload')}
        </Button>
      </Upload>
    </Tooltip>
  );
};

/**
 * Külső webcím egy új, még üres sorhoz. Csak Enterre / fókuszvesztéskor
 * írja vissza az értéket: gépelés közben a sor különben már az első
 * karakternél átváltana kész sorra, és elvenné a mezőt a kéz alól.
 */
const LogoUrlInput: React.FC<RowProps> = ({ onChange }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const commit = () => {
    if (draft.trim()) onChange(draft.trim());
  };

  return (
    <Tooltip title={t('imageUpload.urlHint')}>
      <Input
        value={draft}
        placeholder={t('imageUpload.urlPlaceholder')}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onPressEnter={commit}
      />
    </Tooltip>
  );
};

const SponsorLogoTable: React.FC<SponsorLogoTableProps> = ({ logos, onChange }) => {
  const { t } = useTranslation();

  const setLogo = (index: number, url: string) =>
    onChange((current) => current.map((logo, i) => (i === index ? url : logo)));
  const removeLogo = (index: number) =>
    onChange((current) => current.filter((_, i) => i !== index));

  const columns: ColumnsType<SponsorLogoRow> = [
    {
      key: 'logo',
      title: t('raffle.settings.sponsorLogoColumn'),
      width: THUMBNAIL_SIZE + 32,
      render: (_, { url }) =>
        url ? (
          <Image
            src={url}
            alt={t('imageUpload.alt')}
            width={THUMBNAIL_SIZE}
            height={THUMBNAIL_SIZE}
            style={{ objectFit: 'contain' }}
          />
        ) : (
          <PictureOutlined style={{ fontSize: THUMBNAIL_SIZE / 2, color: '#bfbfbf' }} />
        ),
    },
    {
      key: 'path',
      title: t('raffle.settings.sponsorLogoPath'),
      render: (_, { index, url }) =>
        url ? (
          <Tooltip title={url}>
            <Text>{shortLogoPath(url)}</Text>
          </Tooltip>
        ) : (
          <LogoUrlInput url={url} onChange={(next) => setLogo(index, next)} />
        ),
    },
    {
      key: 'actions',
      align: 'right',
      width: 1,
      render: (_, { index, url }) => (
        <Space>
          <LogoUploadButton url={url} onChange={(next) => setLogo(index, next)} />
          <Button danger icon={<DeleteOutlined />} onClick={() => removeLogo(index)}>
            {t('common.delete')}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Table<SponsorLogoRow>
        size="small"
        columns={columns}
        // The index is the identity: these rows carry no id, and the only
        // reordering possible is a removal, which rebuilds the list anyway.
        rowKey="index"
        dataSource={logos.map((url, index) => ({ index, url }))}
        pagination={false}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: t('raffle.settings.noSponsorLogos') }}
      />
      <Button icon={<PlusOutlined />} onClick={() => onChange((current) => [...current, ''])}>
        {t('raffle.settings.addSponsorLogo')}
      </Button>
    </Space>
  );
};

export default SponsorLogoTable;
